import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { CanvasDocument } from '../../contexts/CanvasPanelContext';

// CDN worker avoids webpack/Babel bundling the CJS worker as a chunk (require is not defined in workers)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// ── Helpers ──────────────────────────────────────────────────────────────────

const IMAGE_MIMES = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp',
]);
const IMAGE_EXTS = /\.(png|jpe?g|gif|svg|webp)(\?.*)?$/i;
const PDF_EXTS = /\.pdf(\?.*)?$/i;

function resolveContentType(doc: CanvasDocument): 'pdf' | 'image' | 'unknown' {
  const mime = doc.mimeType?.toLowerCase() ?? '';
  if (mime === 'application/pdf' || PDF_EXTS.test(doc.uri)) return 'pdf';
  if (IMAGE_MIMES.has(mime) || IMAGE_EXTS.test(doc.uri)) return 'image';
  return 'unknown';
}

// ── Sub-viewers ───────────────────────────────────────────────────────────────

function PdfViewer({ doc }: { doc: CanvasDocument }) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [page, setPage] = useState(doc.page ?? 1);

  useEffect(() => {
    setPage(doc.page ?? 1);
  }, [doc.page, doc.uri]);

  const onLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Page navigation */}
      {numPages !== null && numPages > 1 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.5,
          borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0,
        }}>
          {/* The glyphs are the visual affordance only — a screen reader would
              otherwise announce "left-pointing angle quotation mark". */}
          <IconButton size="small" aria-label="Previous page" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
            <span aria-hidden="true">‹</span>
          </IconButton>
          <Typography variant="caption" sx={{ userSelect: 'none' }}>
            Page {page} / {numPages}
          </Typography>
          <IconButton size="small" aria-label="Next page" onClick={() => setPage(p => Math.min(numPages, p + 1))} disabled={page >= numPages}>
            <span aria-hidden="true">›</span>
          </IconButton>
        </Box>
      )}

      {/* PDF render area */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', p: 1, bgcolor: '#525659' }}>
        <Document
          file={doc.uri}
          onLoadSuccess={onLoadSuccess}
          loading={<Typography sx={{ color: '#fff', p: 2 }}>Loading PDF…</Typography>}
          error={<Typography sx={{ color: '#ffcdd2', p: 2 }}>Failed to load PDF.</Typography>}
        >
          <Page
            pageNumber={page}
            renderTextLayer
            renderAnnotationLayer
            width={undefined}
          />
        </Document>
      </Box>
    </Box>
  );
}

function ImageViewer({ doc }: { doc: CanvasDocument }) {
  return (
    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <img
        src={doc.uri}
        alt={doc.title ?? 'Image'}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </Box>
  );
}


function UnknownViewer({ doc }: { doc: CanvasDocument }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 3 }}>
      <Typography variant="body2" color="text.secondary">
        This file type cannot be previewed inline.
      </Typography>
      <a href={doc.uri} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        Open in new tab <OpenInNewIcon fontSize="small" />
      </a>
    </Box>
  );
}

// ── Download ──────────────────────────────────────────────────────────────────

function deriveFileName(doc: CanvasDocument): string {
  // Prefer the title if it already carries an extension.
  if (doc.title && /\.[a-z0-9]+$/i.test(doc.title)) return doc.title;
  // Otherwise fall back to the last path segment of the URI (minus any query).
  try {
    const path = new URL(doc.uri, window.location.href).pathname;
    const seg = decodeURIComponent(path.split('/').pop() || '');
    if (seg) return seg;
  } catch {
    /* malformed URI — fall through */
  }
  return doc.title || 'download';
}

function DownloadButton({ doc }: { doc: CanvasDocument }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      // Fetch the bytes so the browser saves the file instead of navigating to
      // it. A plain <a download> is ignored for cross-origin URIs, so the blob
      // round-trip is what actually forces the download.
      const res = await fetch(doc.uri);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = objectUrl;
      a.download = deriveFileName(doc);
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // If the fetch is blocked (e.g. CORS), fall back to opening in a new tab.
      window.open(doc.uri, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  }, [doc, downloading]);

  return (
    <Tooltip title="Download">
      <span>
        <IconButton size="small" aria-label="Download document" onClick={handleDownload} disabled={downloading}>
          <DownloadIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface CanvasPanelProps {
  document: CanvasDocument | null;
  onClose(): void;
}

export default function CanvasPanel({ document: doc, onClose }: CanvasPanelProps) {
  if (!doc) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No document loaded.</Typography>
      </Box>
    );
  }

  const contentType = resolveContentType(doc);

  return (
    // Supporting content alongside the conversation, not part of it — a
    // complementary landmark so it can be reached and skipped as a unit.
    // Labelled by its own title so the rotor shows which document is open.
    <Box
      component="aside"
      aria-labelledby="canvas-panel-title"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', overflow: 'hidden' }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 1,
        borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, gap: 1, minHeight: 48,
      }}>
        <Typography
          component="h2"
          id="canvas-panel-title"
          variant="subtitle2"
          noWrap
          title={doc.title}
          sx={{ flex: 1, fontWeight: 600, m: 0 }}
        >
          {doc.title ?? 'Document'}
        </Typography>

        <DownloadButton doc={doc} />

        <Tooltip title="Open in new tab">
          {/* Name matches the tooltip text verbatim so the accessible name
              contains the visible label (WCAG 2.5.3). */}
          <IconButton size="small" aria-label="Open in new tab" component="a" href={doc.uri} target="_blank" rel="noopener noreferrer">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Close">
          <IconButton size="small" aria-label="Close document panel" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {contentType === 'pdf' && <PdfViewer doc={doc} />}
        {contentType === 'image' && <ImageViewer doc={doc} />}
        {contentType === 'unknown' && <UnknownViewer doc={doc} />}
      </Box>
    </Box>
  );
}
